import { Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                    fontSize: 11,
                }}
            >
                {label}
            </p>
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{ margin: "2px 0", color: entry.fill }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

function transformData(rawData) {
    // rawData: [[xLabel, yLabel], [x, y], ...]  (simple 2-col)
    if (!rawData || rawData.length < 2) return [];
    const [headers, ...rows] = rawData;
    const [xKey, yKey] = headers;
    return rows.map((row) => ({
        [xKey]: String(row[0]),
        [yKey]: row[1] === yKey ? yKey : parseFloat(row[1]) || 0,
    }));
}

function Chart2() {
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
    });

    const {
        status: chartStatus,
        data: chartRes,
        refetch: getChart,
    } = useQuery(
        "componet-wise-issue",
        async () =>
            await HTTP("/analytics/getComponentsWiseIssues", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        getChart();
    }, []);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((p) => ({
            ...p,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    useEffect(() => {
        getChart();
    }, [formData]);

    useEffect(() => {
        if (chartStatus === "success") setChartData(chartRes.data);
    }, [chartStatus, chartRes]);

    const parsed = transformData(chartData);
    const isReady = chartStatus === "success" && parsed.length > 0;

    const xKey = chartData[0]?.[0] ?? "Component";
    const yKey = chartData[0]?.[1] ?? "Counts";

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    Component Wise Issues
                </span>
            </div>

            {isReady ? (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={parsed}
                        margin={{ top: 10, right: 16, left: 0, bottom: 60 }}
                        barCategoryGap="35%"
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--color-border-light)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey={xKey}
                            {...axisStyle}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            tick={{
                                fill: "var(--color-text-muted)",
                                fontSize: 10,
                            }}
                        />
                        <YAxis {...axisStyle} allowDecimals={false} />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: "var(--color-primary-light)" }}
                        />
                        <Bar
                            dataKey={yKey}
                            radius={[4, 4, 0, 0]}
                            maxBarSize={52}
                        >
                            {parsed.map((_, i) => (
                                <Cell
                                    key={i}
                                    fill={COLORS[i % COLORS.length]}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 8, width: "100%" }}
                />
            )}
        </div>
    );
}

export default Chart2;
